import { useState, useEffect, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Building2, ChevronLeft, ChevronRight, Download, Edit, Filter, Mail, MessageCircle, Plus, Search, Trash2, UserCheck, Users
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/utils/cn'
import { Client, ClientFilters, PaginationOptions } from '../types/client'
import { clientService } from '../services/client-service'
import { ClientExport } from '../components/ClientExport'
import { WhatsAppLink } from '../components/common/WhatsAppLink'
import { NativeSelect } from '@/components/ui/native-select'
import { getErrorMessage } from '../utils/error.utils'

const CLIENT_STATUS_BADGES: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  ACTIVE: { label: 'Ativo', variant: 'default' },
  PROSPECT: { label: 'Prospect', variant: 'secondary' },
  INACTIVE: { label: 'Inativo', variant: 'outline' },
  BLOCKED: { label: 'Bloqueado', variant: 'destructive' },
}

const STATUS_DOT: Record<string, string> = {
  ACTIVE: 'bg-primary',
  PROSPECT: 'bg-muted-foreground',
  INACTIVE: 'bg-muted-foreground/50',
  BLOCKED: 'bg-destructive',
}

export function ClientsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [infoMessage, setInfoMessage] = useState<string | null>(null)
  const [filters, setFilters] = useState<ClientFilters>({})
  const [pagination, setPagination] = useState<PaginationOptions>({
    page: 1,
    limit: 20,
    sort: 'createdAt:desc'
  })
  const [totalPages, setTotalPages] = useState(0)
  const [showFilters, setShowFilters] = useState(false)
  const [showExport, setShowExport] = useState(false)

  // Show info message from navigation state
  useEffect(() => {
    if (location.state?.message) {
      setInfoMessage(location.state.message)
      // Clear message after 5 seconds
      setTimeout(() => setInfoMessage(null), 5000)
      // Clear location state
      window.history.replaceState({}, document.title)
    }
  }, [location])

  const loadClients = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await clientService.getAllClients(filters, pagination)
      setClients(response.clients)
      setTotalPages(response.pagination.pages)
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar clientes'))
    } finally {
      setLoading(false)
    }
  }, [filters, pagination])

  useEffect(() => {
    loadClients()
  }, [loadClients])

  const handleSearch = (searchTerm: string) => {
    setFilters({ ...filters, search: searchTerm })
    setPagination({ ...pagination, page: 1 })
  }

  const handleFilterChange = (newFilters: Partial<ClientFilters>) => {
    setFilters({ ...filters, ...newFilters })
    setPagination({ ...pagination, page: 1 })
  }

  const clearFilters = () => {
    setFilters({})
    setPagination({ ...pagination, page: 1 })
  }

  const handlePageChange = (page: number) => {
    setPagination({ ...pagination, page })
  }

  const handleDelete = async (clientId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este contato?')) {
      try {
        await clientService.deleteClient(clientId)
        loadClients() // Reload the list
      } catch (err: unknown) {
        setError(getErrorMessage(err, 'Erro ao excluir contato'))
      }
    }
  }

  const getStatusBadge = (status: string) => {
    const badge = CLIENT_STATUS_BADGES[status]
    return badge ? <Badge variant={badge.variant}>{badge.label}</Badge> : null
  }

  const getClientIcon = (clientType: string) => {
    const Icon = clientType === 'PERSON' ? UserCheck : Building2
    return <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
  }

  const getStatusIndicator = (status: string) => (
    <span
      className={cn('inline-block size-2 rounded-full ring-2 ring-card', STATUS_DOT[status] ?? 'bg-muted-foreground/50')}
      title={CLIENT_STATUS_BADGES[status]?.label || status}
    />
  )

  const getClientName = (client: Client) => {
    if (client.clientType === 'PERSON' && client.personalData) {
      return `${client.personalData.firstName} ${client.personalData.lastName}`
    } else if (client.clientType === 'COMPANY' && client.companyData) {
      return client.companyData.tradingName || client.companyData.companyName
    }
    return 'Nome não informado'
  }

  const getPrimaryEmail = (client: Client) => {
    const emailContact = client.contacts.find(c => c.contactType === 'EMAIL' && c.isPrimary)
    return emailContact?.value || null
  }

  const getDocumentNumber = (client: Client) => {
    if (client.clientType === 'PERSON' && client.personalData?.cpf) {
      return client.personalData.cpf
    } else if (client.clientType === 'COMPANY' && client.companyData?.cnpj) {
      return client.companyData.cnpj
    }
    return null
  }

  const renderWhatsAppContact = (client: Client) => {
    const phone = client.whatsappPhone

    if (!phone) {
      return <span className="text-sm text-muted-foreground">Não informado</span>
    }

    return (
      <WhatsAppLink phone={phone} iconSize="sm" asButton={true} />
    )
  }

  const activeFilterCount = Object.values(filters).filter(v => v !== undefined && v !== '').length
  const initialLoading = loading && clients.length === 0

  const openWhatsApp = (rawPhone: string) => {
    let phoneNumber = rawPhone.trim()
    // Se começa com +, é internacional
    if (phoneNumber.startsWith('+')) {
      phoneNumber = phoneNumber.replace(/[^\d+]/g, '').substring(1)
    } else {
      // Número brasileiro
      const cleanPhone = phoneNumber.replace(/\D/g, '')
      phoneNumber = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`
    }
    window.open(`https://wa.me/${phoneNumber}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Users className="size-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold">Clientes</h1>
          </div>
          <p className="text-sm text-muted-foreground">Abra um cliente para ver o dossiê: casos, prazos, financeiro e documentos.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setShowExport(!showExport)} aria-expanded={showExport}>
            <Download className="size-4" /> Exportar
          </Button>
          <Button asChild>
            <Link to="/clients/new"><Plus className="size-4" /> Novo cliente</Link>
          </Button>
        </div>
      </div>

      {infoMessage && (
        <p role="status" className="rounded-md border bg-muted/40 p-3 text-sm">{infoMessage}</p>
      )}

      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-[minmax(0,1fr)_15rem_auto] md:items-center">
          <div className="relative min-w-0">
            <Search aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="client-search"
              type="text"
              aria-label="Buscar clientes"
              placeholder="Buscar clientes..."
              value={filters.search || ''}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-8"
            />
          </div>
          <NativeSelect
            aria-label="Ordenar clientes"
            value={pagination.sort || 'createdAt:desc'}
            onChange={(e) => setPagination({ ...pagination, sort: e.target.value, page: 1 })}
          >
            <option value="name:asc">Nome (A–Z)</option>
            <option value="name:desc">Nome (Z–A)</option>
            <option value="createdAt:desc">Mais recentes</option>
            <option value="createdAt:asc">Mais antigos</option>
            <option value="clientNumber:asc">Nº do contato (crescente)</option>
            <option value="clientNumber:desc">Nº do contato (decrescente)</option>
          </NativeSelect>
          <Button
            variant="outline"
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            aria-expanded={showFilters}
            aria-controls="client-filters-panel"
          >
            <Filter className="size-4" />
            Filtros
            {activeFilterCount > 0 && <Badge variant="secondary">{activeFilterCount}</Badge>}
          </Button>
        </div>

        {showFilters && (
          <Card size="sm" id="client-filters-panel">
            <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] lg:items-end">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="client-filter-type">Tipo</Label>
                <NativeSelect
                  id="client-filter-type"
                  value={filters.clientType || ''}
                  onChange={(e) => handleFilterChange({ clientType: e.target.value as never || undefined })}
                >
                  <option value="">Todos</option>
                  <option value="PERSON">Pessoa Física</option>
                  <option value="COMPANY">Pessoa Jurídica</option>
                </NativeSelect>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="client-filter-status">Status</Label>
                <NativeSelect
                  id="client-filter-status"
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange({ status: e.target.value as never || undefined })}
                >
                  <option value="">Todos</option>
                  <option value="ACTIVE">Ativo</option>
                  <option value="PROSPECT">Prospect</option>
                  <option value="INACTIVE">Inativo</option>
                  <option value="BLOCKED">Bloqueado</option>
                </NativeSelect>
              </div>
              <Button variant="ghost" type="button" onClick={clearFilters} className="sm:col-span-2 lg:col-span-1">
                Limpar Filtros
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {showExport && <ClientExport filters={filters} totalClients={clients.length} />}

      {error && (
        <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>
      )}

      <Card className="py-0">
        {initialLoading ? (
          <div className="flex flex-col gap-2 p-4" aria-busy="true">
            <p className="text-sm text-muted-foreground">Carregando clientes...</p>
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14" />)}
          </div>
        ) : clients.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
            <UserCheck className="size-10 text-muted-foreground" aria-hidden="true" />
            <h3 className="text-base font-medium">Nenhum contato encontrado</h3>
            <p className="text-sm text-muted-foreground">
              {filters.search || Object.keys(filters).length > 0
                ? 'Tente ajustar os filtros de busca'
                : 'Comece adicionando seu primeiro contato'}
            </p>
            <Button asChild className="mt-2">
              <Link to="/clients/new"><Plus className="size-4" /> Novo cliente</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="hidden border-b bg-muted/40 px-4 py-2 md:block">
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-muted-foreground">
                <div className="col-span-6">Cliente</div>
                <div className="col-span-4">Contato</div>
                <div className="col-span-2">Ações</div>
              </div>
            </div>

            <div className="divide-y">
              {clients.map((client) => {
                const email = getPrimaryEmail(client)
                const documentNumber = getDocumentNumber(client)

                return (
                  <Link
                    key={client.id}
                    to={`/clients/${client.id}`}
                    className="block px-4 py-3 transition-colors hover:bg-muted/60"
                  >
                    <div className="flex flex-col gap-3 md:grid md:grid-cols-12 md:gap-4">
                      <div className="min-w-0 md:col-span-6">
                        <div className="flex items-start gap-3">
                          <div className="relative mt-0.5 shrink-0">
                            {getClientIcon(client.clientType)}
                            <div className="absolute -right-0.5 -top-0.5">{getStatusIndicator(client.status)}</div>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="min-w-0 break-words font-medium">{getClientName(client)}</p>
                              {client.status !== 'ACTIVE' && getStatusBadge(client.status)}
                            </div>
                            {documentNumber && <div className="mt-0.5 text-sm text-muted-foreground tabular-nums">{documentNumber}</div>}
                          </div>
                        </div>
                      </div>

                      <div className="min-w-0 md:col-span-4">
                        <div className="flex flex-col gap-1">
                          <div>{renderWhatsAppContact(client)}</div>
                          {email && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                e.preventDefault()
                                window.location.href = `mailto:${email}`
                              }}
                              className="flex min-w-0 items-center gap-2 text-left text-sm text-primary underline-offset-4 hover:underline"
                              title="Enviar email"
                            >
                              <Mail className="size-4 shrink-0" aria-hidden="true" />
                              <span className="min-w-0 break-all">{email}</span>
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="md:col-span-2">
                        <div className="flex items-center gap-1">
                          {client.whatsappPhone && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Abrir WhatsApp"
                                  className="text-muted-foreground"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    e.preventDefault()
                                    openWhatsApp(client.whatsappPhone!)
                                  }}
                                >
                                  <MessageCircle className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Abrir WhatsApp</TooltipContent>
                            </Tooltip>
                          )}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label="Editar"
                                className="text-muted-foreground"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  e.preventDefault()
                                  navigate(`/clients/${client.id}/edit`)
                                }}
                              >
                                <Edit className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Editar</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                aria-label="Excluir"
                                className="text-muted-foreground hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  e.preventDefault()
                                  handleDelete(client.id)
                                }}
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Excluir</TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-3">
                <p className="text-sm text-muted-foreground">Página {pagination.page} de {totalPages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1}>
                    <ChevronLeft className="size-4" /> Anterior
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === totalPages}>
                    Próxima <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
